import React from 'react';
import { Dialog as RadixDialog } from 'radix-ui';
import cn from 'classnames';
import { Button, ButtonProps } from '@base/lib/Button';
import { IconArrow, IconX } from '@base/icon';
import { useTrans } from 'base/utils/i18n';

// ==================== 常量定义 ====================
const ANIMATION_DURATION = 300; // 动画持续时间（ms）
const DEFAULT_WIDTH = 520; // 默认宽度（px）

// ==================== 类型定义 ====================
type ICloseIconType = 'x' | 'arrow';

type DialogHeaderProps = {
    title?: React.ReactNode;
    closeIcon?: boolean | React.ReactNode;
    closeIconType?: ICloseIconType;
    closePlacement?: 'left' | 'right';
    titleVisible?: boolean; // 标题是否可见（但保持可访问性）
};

type DialogBodyProps = {
    className?: string;
    children?: React.ReactNode;
    bodyClassName?: string;
    bodyStyle?: React.CSSProperties;
};

type DialogFooterProps = {
    cancelText?: React.ReactNode | false; // false 表示不显示
    okText?: React.ReactNode | false; // false 表示不显示
    onOk?: () => void | Promise<void>;
    onCancel?: () => void | boolean | Promise<void | boolean>; // 返回 false 阻止关闭
    okButtonProps?: ButtonProps;
    cancelButtonProps?: ButtonProps;
    cancelButtonStyle?: 'default' | 'outline'; // default: 灰色样式, outline: 绿色描边样式
    children?: boolean | React.ReactNode;
};

export type DialogProps = DialogHeaderProps &
    Omit<DialogFooterProps, 'children'> & {
        className?: string;
        style?: React.CSSProperties;
        width?: number | string;
        height?: number | string;
        zIndex?: number; // 自定义 z-index
        afterClose?: () => void; // Dialog 完全关闭后的回调
        bodyClassName?: string;
        bodyStyle?: React.CSSProperties;

        trigger?: React.ReactNode;

        header?: boolean | React.ReactNode; // false 时不渲染默认 header
        body?: boolean | React.ReactNode; // false 时不渲染默认 DialogBody
        footer?: boolean | React.ReactNode; // false 时不渲染默认 footer

        children?: React.ReactNode;

        mask?: boolean;
        maskClosable?: boolean;
        maskClassName?: string;
        maskStyle?: React.CSSProperties;
        keyboard?: boolean; // 是否支持键盘 ESC 关闭

        open?: boolean;
        onOpenChange?: (open: boolean) => void;
        onClose?: () => void;

        defaultOpen?: boolean;
        closable?: boolean; // 兼容属性，控制所有关闭方式
        modal?: boolean; // 是否为模态框

        centered?: boolean; // 是否垂直居中显示
        destroyOnClose?: boolean; // 关闭时销毁内容
        animationDuration?: number; // 动画持续时间

        getContainer?: HTMLElement | (() => HTMLElement); // Portal 容器
    };

// 导出子组件的 Props 类型
export type { DialogHeaderProps, DialogBodyProps, DialogFooterProps };

/**
 * Dialog 包裹层组件
 */
export const DialogWrapper: Globals.FC = ({ className, ...props }) => {
    return (
        <div
            className={cn(className, 'sw-dialog-wrapper flex-1 flex flex-vertical')}
            {...props}
        />
    );
};
DialogWrapper.displayName = 'DialogWrapper';

/**
 * Dialog 头部组件
 */
export const DialogHeader: Globals.FC<DialogHeaderProps> = (props) => {
    const {
        className,
        children,
        title,
        titleVisible = true,
        closeIcon = true,
        closeIconType = 'x',
        closePlacement = 'right',
    } = props;

    // 渲染可见的标题
    const titleNode = React.useMemo(
        () =>
            titleVisible && title ? (
                <div className="sw-dialog-title flex-1">{title}</div>
            ) : (
                <div className="sw-dialog-title flex-1" />
            ),
        [title, titleVisible],
    );

    // 如果使用了自定义 children，优先渲染 children
    if (!!children && children !== true) {
        return (
            <div
                className={cn(className, 'sw-dialog-header flex flex-middle', {
                    'flex-reverse': closePlacement === 'right',
                })}
            >
                {titleNode}
                {children}
            </div>
        );
    }

    // 渲染默认布局
    return (
        <div
            className={cn(className, 'sw-dialog-header flex flex-middle', {
                'flex-reverse': closePlacement === 'right',
            })}
        >
            {closeIcon && (
                <DialogClose>
                    <DialogHeaderButton>
                        {typeof closeIcon !== 'boolean' ? (
                            closeIcon
                        ) : closeIconType === 'x' ? (
                            <IconX size={false} />
                        ) : (
                            <IconArrow direction={'left'} />
                        )}
                    </DialogHeaderButton>
                </DialogClose>
            )}

            {titleNode}
        </div>
    );
};
DialogHeader.displayName = 'DialogHeader';

/**
 * Dialog 标题组件
 */
export const DialogTitle: Globals.FC<{ visible?: boolean }> = ({
    children,
    visible = true,
}) => {
    return (
        <RadixDialog.Title
            id="dialog-title"
            className={cn('sw-dialog-title flex-1', { 'visually-hidden': !visible })}
        >
            {children}
        </RadixDialog.Title>
    );
};
DialogTitle.displayName = 'DialogTitle';

/**
 * Dialog 关闭触发器组件
 */
export const DialogClose: Globals.FC = ({ children }) => {
    return <RadixDialog.Close asChild>{children}</RadixDialog.Close>;
};
DialogClose.displayName = 'DialogClose';

/**
 * Dialog 头部按钮组件
 */
export const DialogHeaderButton = React.forwardRef<
    HTMLButtonElement,
    ButtonProps
>(({ className, type, ...props }, ref) => {
    return (
        <Button
            {...props}
            ref={ref}
            className={cn(className, 'sw-dialog-header-btn h-full')}
            type={'no-style'}
        />
    );
});
DialogHeaderButton.displayName = 'DialogHeaderButton';

/**
 * Dialog 底部组件
 */
export const DialogFooter: Globals.FC<
    DialogFooterProps & { onClose?: () => void; open?: boolean; loading?: boolean }
> = ({ className, open, loading, onClose, ...footerProps }) => {
    const {
        cancelText = false,
        okText,
        onOk,
        onCancel,
        okButtonProps,
        cancelButtonProps,
        cancelButtonStyle = 'default',
        children,
    } = footerProps;

    const { t } = useTrans();

    // 使用默认文案（仅在值为 undefined 时）
    const finalOkText = okText === undefined ? t('web_common_btn_ok') : okText;
    const finalCancelText =
        cancelText === undefined ? t('web_common_btn_cancel') : cancelText;

    // 判断是否有按钮需要渲染
    const hasButtons =
        (cancelText !== false && cancelText !== undefined) ||
        (okText !== false && okText !== undefined);

    // 如果没有任何按钮且 children 为空，则不渲染 footer
    if (!hasButtons && (!children || children === true)) {
        return null;
    }

    // 根据 cancelButtonStyle 决定取消按钮的类名
    const cancelBtnClassName = cancelButtonStyle === 'outline' 
        ? 'sw-dialog-btn-outline' 
        : 'sw-dialog-btn-default';

    return (
        <div className={cn(className, 'sw-dialog-footer flex gap-12')}>
            {!!children && children !== true ? (
                children
            ) : (
                <>
                    {cancelText !== false && (
                        <DialogFooterButton
                            {...cancelButtonProps}
                            onClick={onCancel}
                            type="default"
                            className={cn(cancelButtonProps?.className, cancelBtnClassName)}
                        >
                            {finalCancelText}
                        </DialogFooterButton>
                    )}
                    {okText !== false && (
                        <DialogFooterButton
                            {...okButtonProps}
                            onClick={onOk}
                            loading={loading || okButtonProps?.loading}
                            type="primary"
                            className={cn(okButtonProps?.className, 'sw-dialog-btn-primary')}
                        >
                            {finalOkText}
                        </DialogFooterButton>
                    )}
                </>
            )}
        </div>
    );
};
DialogFooter.displayName = 'DialogFooter';

/**
 * Dialog 底部按钮组件
 */
export const DialogFooterButton: Globals.FC<ButtonProps> = ({
    className,
    ...props
}) => {
    return <Button {...props} className={cn(className, 'flex-1')} />;
};
DialogFooterButton.displayName = 'DialogFooterButton';

/**
 * Dialog 内容区域组件
 */
export const DialogBody: Globals.FC<DialogBodyProps> = ({
    className,
    children,
    bodyStyle,
}) => {
    return (
        <div
            className={cn(className, 'sw-dialog-body flex-1 overflow-y-auto')}
            style={bodyStyle}
        >
            {children}
        </div>
    );
};
DialogBody.displayName = 'DialogBody';

// ==================== 主组件 ====================

/**
 * Dialog 对话框组件
 *
 * 基于 Radix UI Dialog 封装，提供与 Drawer 一致的 API
 *
 * @example
 * ```tsx
 * <Dialog
 *   trigger={<Button>打开</Button>}
 *   title="标题"
 *   body="内容"
 * />
 * ```
 */
export const Dialog = React.forwardRef<HTMLDivElement, DialogProps>(
    ({ className, style, children, ...props }, ref) => {
        const {
            width = DEFAULT_WIDTH,
            height,
            zIndex,
            trigger,
            header = true,
            body,
            footer = true,
            title,

            closeIcon = false,
            closeIconType = 'x',
            closePlacement,
            titleVisible,
            cancelText,
            okText,
            onOk,
            onCancel,
            okButtonProps,
            cancelButtonProps,
            cancelButtonStyle,
            bodyClassName,
            bodyStyle,

            mask = true,
            maskClosable = true,
            maskClassName,
            maskStyle,
            keyboard = true,

            open,
            onOpenChange,
            onClose,

            defaultOpen = false,
            closable, // 兼容属性
            modal = true,

            centered = true,
            destroyOnClose = false,
            animationDuration = ANIMATION_DURATION,
            getContainer,
            afterClose,
        } = props;

        // 兼容 closable 属性（优先级低于细粒度控制）
        const finalKeyboard = closable === false ? false : keyboard;
        const finalMaskClosable = closable === false ? false : maskClosable;

        // 确定关闭图标的可见性和类型
        const resolvedCloseIcon =
            closable === false && closeIcon === undefined
                ? false
                : closeIcon === undefined
                    ? true
                    : closeIcon;

        // 决定是否绝对定位渲染关闭图标（在 Header 外部）
        // 如果是默认的 'x' 类型，或者是没有 Header 的情况，都使用绝对定位渲染
        // 如果是 'arrow' 类型（如返回按钮）且有 Header，则保留在 Header 内部
        const isAbsoluteClose =
            resolvedCloseIcon && (closeIconType === 'x' || !header);

        // destroyOnClose 时的初始挂载状态
        const [mounted, setMounted] = React.useState(
            !destroyOnClose || !!(open ?? defaultOpen),
        );

        // 保存动画时长的引用，避免作为依赖项
        const animationDurationRef = React.useRef(animationDuration);
        animationDurationRef.current = animationDuration;

        // 处理挂载/卸载逻辑
        React.useEffect(() => {
            if (open) {
                setMounted(true);
            } else if (open === false) {
                const timer = setTimeout(() => {
                    if (destroyOnClose) {
                        setMounted(false);
                    }
                    afterClose?.();
                }, animationDurationRef.current);
                return () => clearTimeout(timer);
            }
        }, [open, destroyOnClose, afterClose]);

        // 内部状态控制（当未传入 open 属性时）
        const [internalOpen, setInternalOpen] = React.useState(defaultOpen);

        // 是否受控
        const isControlled = open !== undefined;

        // 最终的 open 状态
        const finalOpen = isControlled ? open : internalOpen;

        // 处理 open 状态变化
        const handleOpenChange = React.useCallback(
            (newOpen: boolean) => {
                // 如果非受控，更新内部状态
                if (!isControlled) {
                    setInternalOpen(newOpen);
                }

                onOpenChange?.(newOpen);
                if (!newOpen) {
                    onClose?.();
                }
            },
            [isControlled, onOpenChange, onClose],
        );

        // Portal 容器（缓存）
        const [containerElement, setContainerElement] =
            React.useState<HTMLElement | null>(null);

        React.useEffect(() => {
            if (getContainer) {
                setContainerElement(
                    typeof getContainer === 'function' ? getContainer() : getContainer,
                );
            }
        }, [getContainer]);

        // 内部 Loading 状态
        const [confirmLoading, setConfirmLoading] = React.useState(false);

        // 处理确定
        const handleOk = async () => {
            // 防止多次触发
            if (confirmLoading) return;

            if (!onOk) {
                handleOpenChange(false);
                return;
            }

            setConfirmLoading(true);
            try {
                await onOk();
                handleOpenChange(false);
            } catch (error) {
                console.error('Dialog onOk error:', error);
            } finally {
                setConfirmLoading(false);
            }
        };

        // 处理取消（包括 Mask 点击、ESC、取消按钮）
        const handleCancel = async (e?: React.SyntheticEvent | Event) => {
            if (onCancel) {
                try {
                    const result = await onCancel();
                    if (result === false) return;
                } catch (error) {
                    console.error('Dialog onCancel error:', error);
                    return;
                }
            }
            handleOpenChange(false);
        };

        // 事件处理器
        const handlePointerDownOutside = (event: Event) => {
            // 如果不允许 Mask 关闭，直接阻止
            if (!finalMaskClosable) {
                event.preventDefault();
                return;
            }

            // 如果允许关闭，但有 onCancel 逻辑，需要拦截处理
            if (onCancel) {
                event.preventDefault(); // 先阻止默认关闭
                // 使用 Promise.resolve 确保同步执行，避免竞态
                void handleCancel(event);
            }
            // 如果没有 onCancel，直接放行，让 Radix 处理关闭和焦点归还
        };

        const handleEscapeKeyDown = (event: KeyboardEvent) => {
            if (!finalKeyboard) {
                event.preventDefault();
                return;
            }

            if (onCancel) {
                event.preventDefault();
                void handleCancel(event);
            }
        };

        const handleInteractOutside = (event: Event) => {
            // 主要是为了防止焦点跑出去
            // 如果不允许 Mask 关闭，则阻止交互
            if (!finalMaskClosable) {
                event.preventDefault();
            }
        };

        // Dialog 内容渲染
        const dialogContent = (
            <>
                {mask && (
                    <RadixDialog.Overlay
                        className={cn(maskClassName, 'sw-dialog-mask')}
                        style={maskStyle}
                    />
                )}

                <RadixDialog.Content
                    ref={ref}
                    onPointerDownOutside={handlePointerDownOutside}
                    onEscapeKeyDown={handleEscapeKeyDown}
                    aria-describedby={undefined}
                    className={cn(className, 'sw-dialog flex flex-vertical', {
                        'sw-dialog-centered': centered,
                    })}
                    style={{
                        width: typeof width === 'number' ? `${width}px` : width,
                        height: typeof height === 'number' ? `${height}px` : height,
                        ...(zIndex !== undefined ? { zIndex } : {}),
                        ...style,
                    }}
                >
                    {/* 始终渲染隐藏的 Title 以满足无障碍要求 */}
                    <span className="visually-hidden">
                        <RadixDialog.Title>{title || 'Dialog'}</RadixDialog.Title>
                    </span>

                    {/* 始终渲染隐藏的 Description 以满足无障碍要求 */}
                    <span className="visually-hidden">
                        <RadixDialog.Description>Dialog content</RadixDialog.Description>
                    </span>

                    {mounted && (
                        <DialogWrapper>
                            {/* Header */}
                            {header !== false && (
                                <DialogHeader
                                    title={title}
                                    closeIcon={!isAbsoluteClose && resolvedCloseIcon}
                                    closeIconType={closeIconType}
                                    closePlacement={closePlacement}
                                    titleVisible={titleVisible}
                                >
                                    {header !== true ? header : undefined}
                                </DialogHeader>
                            )}

                            {/* Absolute Close Icon */}
                            {isAbsoluteClose && (
                                <div className="sw-dialog-close-x">
                                    <DialogClose>
                                        <Button type="no-style" className="sw-dialog-header-btn">
                                            {typeof resolvedCloseIcon !== 'boolean' ? (
                                                resolvedCloseIcon
                                            ) : (
                                                <IconX size={false} />
                                            )}
                                        </Button>
                                    </DialogClose>
                                </div>
                            )}

                            {/* Body */}
                            {body ? (
                                <DialogBody
                                    className={cn(bodyClassName, {
                                        'sw-dialog-body-no-header': header === false,
                                    })}
                                    bodyStyle={bodyStyle}
                                >
                                    {body}
                                </DialogBody>
                            ) : children ? (
                                <div
                                    className={cn('sw-dialog-body flex-1', bodyClassName, {
                                        'sw-dialog-body-no-header': header === false,
                                    })}
                                    style={bodyStyle}
                                >
                                    {children}
                                </div>
                            ) : null}

                            {/* Footer */}
                            {footer !== false && (
                                <DialogFooter
                                    cancelText={cancelText}
                                    okText={okText}
                                    onOk={handleOk}
                                    onCancel={handleCancel}
                                    okButtonProps={okButtonProps}
                                    cancelButtonProps={cancelButtonProps}
                                    cancelButtonStyle={cancelButtonStyle}
                                    onClose={() => handleOpenChange(false)}
                                    open={open}
                                    loading={confirmLoading}
                                >
                                    {footer !== true ? footer : undefined}
                                </DialogFooter>
                            )}
                        </DialogWrapper>
                    )}
                </RadixDialog.Content>
            </>
        );

        return (
            <RadixDialog.Root
                open={finalOpen}
                onOpenChange={handleOpenChange}
                modal={modal}
            >
                {!!trigger && (
                    <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>
                )}

                {containerElement ? (
                    <RadixDialog.Portal container={containerElement}>
                        {dialogContent}
                    </RadixDialog.Portal>
                ) : (
                    <RadixDialog.Portal>{dialogContent}</RadixDialog.Portal>
                )}
            </RadixDialog.Root>
        );
    },
);
Dialog.displayName = 'Dialog';
